import { useEffect, useState } from 'react';

/**
 * Tracks the iOS / Android on-screen keyboard via window.visualViewport.
 *
 * Returns:
 *  - keyboardHeight: number of CSS pixels the keyboard is covering
 *  - isKeyboardOpen: convenience boolean (keyboardHeight > 0)
 *
 * Use to apply `paddingBottom: keyboardHeight` (or transform) on bottom
 * sheets / dialog scroll containers so inputs stay above the keyboard.
 * This is the ONLY reliable way to handle the iOS keyboard on web.
 */
export function useVisualViewportInset(): {
  keyboardHeight: number;
  isKeyboardOpen: boolean;
} {
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const vv = window.visualViewport;
    if (!vv) return;

    let rafId: number | null = null;

    const update = () => {
      rafId = null;
      // Difference between layout viewport and visual viewport ≈ keyboard height.
      const diff = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      // Filter sub-pixel noise + iOS URL bar collapse (< ~80px is the bar, not a keyboard).
      setKeyboardHeight(diff > 80 ? Math.round(diff) : 0);
    };

    const onChange = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(update);
    };

    vv.addEventListener('resize', onChange);
    vv.addEventListener('scroll', onChange);
    update();

    return () => {
      vv.removeEventListener('resize', onChange);
      vv.removeEventListener('scroll', onChange);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
  }, []);

  return { keyboardHeight, isKeyboardOpen: keyboardHeight > 0 };
}

/**
 * Style helper for mobile bottom sheets that contain content BELOW the
 * focused input (sign-in OAuth buttons, footer CTAs, secondary actions).
 *
 * At rest: sheet hugs its content (capped by safe-area).
 * Keyboard open: sheet snaps to TRUE fullscreen (fixed inset:0, 100dvh,
 * no rounded top) and pads bottom past the keyboard, so the entire form
 * remains reachable by scroll instead of being clipped.
 *
 * Spread the result onto `<SheetContent style={...}>`.
 */
export function useKeyboardAwareSheetStyle(): React.CSSProperties {
  const { keyboardHeight, isKeyboardOpen } = useVisualViewportInset();
  if (isKeyboardOpen) {
    return {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      height: '100dvh',
      maxHeight: '100dvh',
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
      paddingBottom: `calc(${keyboardHeight}px + env(safe-area-inset-bottom) + 16px)`,
    };
  }
  return {
    maxHeight: `calc(100dvh - env(safe-area-inset-top) - 8px)`,
    paddingBottom: `max(1.25rem, env(safe-area-inset-bottom))`,
  };
}

/**
 * Companion to useKeyboardAwareSheetStyle: hide the grab pill while the
 * keyboard is open (sheet is fullscreen, drag-down would conflict with
 * keyboard scroll). Spread the result onto `<SheetContent>`.
 *
 *   const kbStyle = useKeyboardAwareSheetStyle();
 *   const kbChrome = useKeyboardAwareSheetChrome();
 *   <SheetContent {...kbChrome} style={kbStyle}>
 */
export function useKeyboardAwareSheetChrome(): { grabbable: boolean } {
  const { isKeyboardOpen } = useVisualViewportInset();
  return { grabbable: !isKeyboardOpen };
}

